import { pick, last } from 'lodash';
import up = require('updeep');
import { patch } from './patch';
import { CreateUserParams, CreateSessionParams, someMessageForValidationErrors, CompetitionJoinParams, MembershipLevel, EntityMembership, UserRead, DefaultApi, Configuration } from '../../userApi';
import { State } from '../state';
import { Dispatch } from 'redux';
import { LoginForm, signupVEMessages, loginVEMessages } from '../../utils/forms';
import { handleUserApiError, handleUnexpectedError } from './handleUnexpectedError';
import logger from '../../utils/logger';
import * as selectors from '../selectors';
import { userApiBasePath } from '../selectors/userApi';
import { safeJsonFetch } from '../../utils/safeFetch';
import { getValidationErrors, someMatchingEmailDomain } from '../../utils/userApiHelper';
import { resolveCompetitionName, persistCompetitionSlug } from '../../utils/routerHelper';
import { ExtraArguments } from '../extraArguments';
import { toast } from './toast';
import { StyleState } from '../../utils/styles';
import { intercom } from '../../utils/intercom';
import { trackEvent } from '../../utils/googleAnalytics';
import { setAnalyticsAttributes } from './initialize';

const updateIntercom = (state: State) => {
  const { user, competitionUser } = state;
  const university = selectors.userUniversity(state);
  intercom('update', {
    user, university, competitionUser
  });
};

const apiWithToken = (state: State, token: string) =>
  new DefaultApi(new Configuration({
    apiKey: token,
    basePath: userApiBasePath(state)
  }), userApiBasePath(state), safeJsonFetch);

export const login = (loginForm: CreateSessionParams) => async (dispatch: Dispatch, getState: () => State, { i18n, history }: ExtraArguments) => {
  dispatch(patch({ spinner: true }));
  try {
    const params = pick(loginForm, ['email', 'password']) as LoginForm;
    const state = getState();
    params.competitionName = resolveCompetitionName(history.location, i18n, state.initialQuery);
    if (!params.competitionName) {
      dispatch(patch({ spinner: false }));
      toast({
        message: 'Could not determine competition from URL. Open /local-dev-challenge/login',
        style: StyleState.Danger
      })(dispatch, getState);
      return;
    }
    const api = selectors.userApi(state);
    let dashboard = await api.postSession(params);
    if (!dashboard.competitionUser && params.competitionName && dashboard.token) {
      const meDashboard = await apiWithToken(state, dashboard.token).getMe(undefined, params.competitionName);
      dashboard = { ...meDashboard, token: dashboard.token };
    }
    dispatch(patch({
      ...dashboard,
      spinner: false
    }));
    trackEvent('session', 'create');
    const loggedInState = getState();
    updateIntercom(loggedInState);
    setAnalyticsAttributes(loggedInState);
    logger.configure(loggedInState);
    if (params.competitionName) {
      persistCompetitionSlug(params.competitionName);
    }
    if (params.competitionName && history.location.pathname.indexOf('/login') >= 0) {
      history.push(`/${params.competitionName}`);
    }
  } catch (err) {
    const validationErrors = await getValidationErrors(err);
    if (validationErrors && someMessageForValidationErrors(validationErrors, loginVEMessages)) {
      dispatch(patch({
        loginVE: up.constant(validationErrors),
        spinner: false
      }));
      return;
    }
    handleUserApiError(err)(dispatch, getState);
    trackEvent('session', 'unhandledError', 'create');
  }
};

export const signup = (params: CreateUserParams) => async (dispatch: Dispatch, getState: () => State, { i18n }: ExtraArguments) => {
  const t = i18n.getFixedT(i18n.language, 'auth');
  const state = getState();
  const { initialQuery, competition } = state;
  const api = selectors.userApi(state);

  dispatch(patch({ spinner: true }));
  try {
    params.competitionName = state.initialQuery.competitionName;
    if (initialQuery.invitationToken) {
      params.invitationToken = initialQuery.invitationToken;
    }

    if (!someMatchingEmailDomain(params.user.email, competition.permittedDomains)) {
      dispatch(patch({ spinner: false }));
      toast({
        message: t('You must sign up with a permitted email domain.'),
        style: StyleState.Danger
      })(dispatch, getState);
      return;
    }
    const dashboard = await api.postUsers(params);
    dispatch(patch({
      ...dashboard,
      spinner: false
    }));
    const signedupState = getState();
    updateIntercom(signedupState);
    trackEvent('account', 'create');
    logger.configure(signedupState);
  } catch (err) {
    const validationErrors = await getValidationErrors(err);
    if (validationErrors && someMessageForValidationErrors(validationErrors, signupVEMessages)) {
      dispatch(patch({
        signupVE: up.constant(validationErrors),
        spinner: false
      }));
      return;
    }
    handleUnexpectedError(err)(dispatch, getState);
    trackEvent('account', 'unhandledError', 'create');
  }
};

export const logout = () => async (_dispatch: Dispatch, getState: () => State) => {
  const api = selectors.userApi(getState());
  try {
    await api.deleteSession();
    intercom('shutdown', {});
    trackEvent('session', 'delete');
  } catch (err) {
    logger.error(err);
    trackEvent('session', 'unhandledError', 'delete');
  }
  const state = getState();
  const competitionName = (state.competition && state.competition.name) ||
    state.initialQuery.competitionName ||
    '';
  localStorage.removeItem('redux');
  localStorage.removeItem('competitionName');
  persistCompetitionSlug(competitionName);
  const landingPath = competitionName ? `/${competitionName}` : '/';
  if (window.location.pathname === landingPath || window.location.pathname === `${landingPath}/`) {
    window.location.reload();
  } else {
    window.location.pathname = landingPath;
  }
};

export const join = (signupTeam?: string) => async (dispatch: Dispatch, getState: () => State, { i18n, history }: ExtraArguments) => {
  const state = getState();
  const api = selectors.userApi(state);
  const competitionName = resolveCompetitionName(history.location, i18n, state.initialQuery);
  if (!competitionName) {
    dispatch(patch({ spinner: false }));
    return;
  }
  try {
    const body: CompetitionJoinParams = { competitionName };
    if (signupTeam) {
      (body as CompetitionJoinParams & { signupTeam?: string }).signupTeam = signupTeam;
    }
    const dashboard = await api.postJoin(body);
    dispatch(patch({
      ...dashboard,
      spinner: false
    }));
    trackEvent('session', 'join');
    const loggedInState = getState();
    updateIntercom(loggedInState);
    setAnalyticsAttributes(loggedInState);
    logger.configure(loggedInState);
    history.push(`/${competitionName}`);
  } catch (err) {
    const validationErrors = await getValidationErrors(err);
    if (validationErrors && someMessageForValidationErrors(validationErrors, loginVEMessages)) {
      dispatch(patch({
        joinVE: up.constant(validationErrors),
        spinner: false
      }));
      return;
    }
    handleUserApiError(err)(dispatch, getState);
    trackEvent('session', 'unhandledError', 'join');
  }
};
